import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { getNetworkTrafficLiveData } from '../../redux/actions/networkActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Table from '../../components/Table';
// import TextInput from '../../components/TextInput';
import Pagination from '../../components/Pagination';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import NetworkTrafficStyles from './NetworkTrafficLive.module.css';

const FRONTEND_NETWORK_TRAFFIC_LIVE_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildNetworkTrafficLiveSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const NetworkTrafficLiveScreen = () => {
	const dispatch = useDispatch();
	const [ searchKey, setSearchKey ] = React.useState('');

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ networkData, setNetworkData ] = React.useState({});
	const [ networkTotalRows, setNetworkTotalRows ] = React.useState(0);
	const [ networkRows, setNetworkRows ] = React.useState([]);
	const [ hasError, setHasError ] = React.useState(false);
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const [ pageNumber, setPageNumber ] = React.useState(1);
	const pageLimit = 21;

	const setNetworkTrafficLiveSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setHasError(true);
		setSupportErrorMessage(buildNetworkTrafficLiveSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearNetworkTrafficLiveSupportError = React.useCallback(() => {
		setHasError(false);
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [supportErrorMessage]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearNetworkTrafficLiveSupportError();

				try {
					const data = await dispatch(getNetworkTrafficLiveData());
					if (data && data.status) {
						setNetworkData(data);
						clearNetworkTrafficLiveSupportError();
					} else {
						setNetworkData({});
						setNetworkTrafficLiveSupportError(
							FRONTEND_NETWORK_TRAFFIC_LIVE_ERROR_CODES.INITIAL_LOAD_FAILED,
							'Live Traffic Page / Initial Data Load Failed',
							data?.msg || 'Live traffic request did not return success'
						);
					}
				} catch (error) {
					setNetworkData({});
					setNetworkTrafficLiveSupportError(
						FRONTEND_NETWORK_TRAFFIC_LIVE_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Live Traffic Page / Initial Data Request Error',
						error?.message || String(error)
					);
				}
				setDataLoading(false);
			};

			getData();
		},
		[ dispatch, clearNetworkTrafficLiveSupportError, setNetworkTrafficLiveSupportError ]
	);

	React.useEffect(
		() => {
			if (networkData && Object.keys(networkData).length > 0 && networkData.NetRows.length > 0) {
				if (searchKey) {
					// Safe filtering: use string includes instead of regex, check for null/undefined
					const netRows = networkData.NetRows.filter((row) => 
						row.some((el) => el && el.toString().toLowerCase().includes(searchKey.toLowerCase()))
					);
					setNetworkTotalRows(netRows.length);
					setNetworkRows(netRows.slice(0, pageLimit));
				} else {
					setNetworkTotalRows(networkData.NetRows.length);
					setNetworkRows(networkData.NetRows.slice(0, pageLimit));
				}
			} else {
				setNetworkTotalRows(0);
				setNetworkRows([]);
			}
		},
		[ networkData, searchKey ]
	);

	const handlePageChange = async (currentPage) => {
		setPageNumber(currentPage.selected + 1);

		const nextContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		
		if (networkData && Object.keys(networkData).length > 0 && networkData.NetRows.length > 0) {
			let filteredRows = networkData.NetRows;
			
			if (searchKey) {
				// Apply the same safe filtering logic as in useEffect
				filteredRows = networkData.NetRows.filter((row) => 
					row.some((el) => el && el.toString().toLowerCase().includes(searchKey.toLowerCase()))
				);
			}
			
			setNetworkRows(filteredRows.slice(nextContentFrom, nextContentTo));
		}
	};

	return (
		<Content
			currentMenu='network'
			headerTitle='Live Traffic'
			searchBox={true}
			searchPlaceholder='Search here'
			searchValue={searchKey}
			searchOnChange={(e) => setSearchKey(e.target.value)}>
			{dataLoading ? (
				<Loader />
			) : networkData && Object.keys(networkData).length === 0 ? (
				<>
					<NetworkError />
					{supportErrorMessage && (
						<div className={NetworkTrafficStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={NetworkTrafficStyles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={NetworkTrafficStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</>
			) : (
				<Fragment>
					{hasError && supportErrorMessage && (
						<div className={NetworkTrafficStyles.supportErrorBanner}>
							<div className={NetworkTrafficStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={NetworkTrafficStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={NetworkTrafficStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
					{/* <div className={NetworkTrafficStyles.filterContainer}>
						<div className={NetworkTrafficStyles.filterContainer_box} style={{ alignItems: 'flex-end' }}>
							<TextInput
								label='Search'
								icon='search'
								value={searchKey}
								onChange={(e) => setSearchKey(e.target.value)}
								placeholder='Search here'
							/>
							<Button label='Filter' />
						</div>
					</div> */}

					<Table
						containerStyle={{ height: '75vh' }}
						tableHead={
							<tr>
								{networkData.NetCols &&
									networkData.NetCols.length > 0 &&
									networkData.NetCols.map((col, index) => <th key={col + index}>{col}</th>)}
							</tr>
						}
						tableRow={
							networkTotalRows > 0 && networkRows.length > 0 ? (
								networkRows.map((rows, index) => (
									<tr key={index}>{rows && rows.length > 0 && rows.map((row) => <td key={row}>{row}</td>)}</tr>
								))
							) : (
								<tr>
									<td colSpan={networkData.NetCols.length} style={{ textAlign: 'center' }}>
										No data found
									</td>
								</tr>
							)
						}
					/>

					<div className='paginationContainer'>
						<Pagination
							onPageChange={handlePageChange}
							rowsPerPage={pageLimit}
							totalPageSize={networkTotalRows}
							pageNumber={pageNumber}
						/>
					</div>
				</Fragment>
			)}
		</Content>
	);
};

export default NetworkTrafficLiveScreen;
